import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["item", "onShare"],
  _excluded2 = ["renderSubmenuRow", "item"];
import I18n from 'I18n';
import FloatingAlertStore from 'UIComponents/alert/FloatingAlertStore';
import { buildReferralLink, getLessonShareUrl, getPlaylistUrl, getTrackShareUrl } from '../../../utils/RoutingUtils';
import UsageTracker from '../../../utils/UsageTracker';
import { useUserInfo } from '../../accounts';
import { DuplicateIcon, SocialXIcon, SocialLinkedinIcon, SocialWhatsappIcon, SocialFacebookIcon } from 'foundations-assets';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Link } from 'foundations-components/transitional/components/Link';
import { getShortContentUrl } from '../../shortContent/shortContentRouting';
import { useSocialShareData } from '../socialShareHooks';
import { getShareLinks } from 'academy-common-lib/utils/url';
import { useFullLesson } from '../../lessons';
import { useFullTrack } from '../../tracks';
import { IconHolder } from 'foundations-components/transitional/components/IconHolder';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/transitional/components/View';
import { CopyButton } from 'foundations-components/transitional/components/CopyButton';
import { useMemo } from 'react';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useReferalLink = ({
  id,
  type,
  language
}, skip) => {
  const {
    userInfo
  } = useUserInfo();
  const userId = Number(userInfo && userInfo.user.id);
  const {
    lesson,
    lessonLoading
  } = useFullLesson(id, language, skip || type !== 'LESSON' || !language);
  const {
    track,
    trackLoading
  } = useFullTrack(id, language, skip || type !== 'TRACK' || !language);
  const loading = lessonLoading || trackLoading;
  const link = useMemo(() => {
    if (skip) return null;
    switch (type) {
      case 'PLAYLIST':
      case 'LEARNING_PATH':
        return buildReferralLink(getPlaylistUrl(id), userId);
      case 'LESSON':
        return getLessonShareUrl(lesson, userId, id, language);
      case 'TRACK':
        return getTrackShareUrl(track, userId, id, language);
      case 'SHORT_FORM_CONTENT':
        return buildReferralLink(getShortContentUrl({
          shortId: id
        }).fullUrl, userId);
      default:
        return null;
    }
  }, [skip, type, id, userId, lesson, language, track]);
  return {
    link,
    loading
  };
};
const useShareLink = (item, skip) => {
  const {
    link,
    loading: loadingReferalLink
  } = useReferalLink(item, skip);
  const {
    template: referalTemplate,
    loading: loadingSocialShareData,
    isShareable
  } = useSocialShareData(item, skip);
  const template = referalTemplate ? `${referalTemplate}: ` : '';
  const loading = loadingReferalLink || loadingSocialShareData;
  const shareLinks = useMemo(() => link && isShareable ? Object.assign({
    link
  }, getShareLinks(link, template)) : null, [isShareable, link, template]);
  return {
    shareLinks,
    loading
  };
};
const SocialShareListOption = ({
  onClick,
  href,
  renderShareRow = button => button,
  rowSpaceStyleProps,
  children
}) => renderShareRow(href ? /*#__PURE__*/_jsx(Link, Object.assign({
  display: "block",
  href: href,
  onClick: onClick,
  target: "_blank",
  use: "unstyled"
}, rowSpaceStyleProps, {
  children: children
})) : children);
const SocialShareIcon = ({
  icon
}) => {
  return /*#__PURE__*/_jsx(IconHolder, {
    color: "text-core-default",
    icon: icon,
    padding: "0",
    paddingInlineEnd: "200",
    size: "300"
  });
};
const getSocialShareListOptions = (_ref, {
  link,
  linkForFacebook,
  linkForLinkedIn,
  linkForWhatsapp,
  linkForX
}) => {
  let {
      item,
      onShare
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  const handleOnClick = (shareType, url) => {
    UsageTracker.track('interaction', {
      action: 'social-share',
      to: shareType,
      contentId: item.id,
      contentType: item.type,
      playlistId: item.playlistId,
      trackId: item.trackId
    });
    if (onShare) onShare(shareType, url);
  };
  return [/*#__PURE__*/_jsx(SocialShareListOption, Object.assign({}, props, {
    children: /*#__PURE__*/_jsx(CopyButton, Object.assign({
      onClick: () => {
        FloatingAlertStore.addAlert({
          type: 'success',
          message: I18n.text('learningCenter.shareThis.notification')
        });
        handleOnClick('link');
      },
      padding: "0",
      responsive: true,
      tooltipPlacement: "right",
      use: "unstyled",
      value: link
    }, props.rowSpaceStyleProps, {
      children: /*#__PURE__*/_jsxs(View, {
        color: "text-core-default",
        inlineSize: "100%",
        textAlign: "start",
        children: [/*#__PURE__*/_jsx(SocialShareIcon, {
          icon: DuplicateIcon
        }), /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.socialShare.copyLink"
        })]
      })
    }))
  }), "copy-link"), /*#__PURE__*/_jsxs(SocialShareListOption, Object.assign({
    href: linkForX,
    onClick: () => handleOnClick('x', linkForX)
  }, props, {
    children: [/*#__PURE__*/_jsx(SocialShareIcon, {
      icon: SocialXIcon
    }), /*#__PURE__*/_jsx(FormattedMessage, {
      message: "learningCenter.socialShare.shareX"
    })]
  }), "share-x"), /*#__PURE__*/_jsxs(SocialShareListOption, Object.assign({
    href: linkForFacebook,
    onClick: () => handleOnClick('facebook', linkForFacebook)
  }, props, {
    children: [/*#__PURE__*/_jsx(SocialShareIcon, {
      icon: SocialFacebookIcon
    }), /*#__PURE__*/_jsx(FormattedMessage, {
      message: "learningCenter.socialShare.shareFacebook"
    })]
  }), "share-facebook"), /*#__PURE__*/_jsxs(SocialShareListOption, Object.assign({
    href: linkForLinkedIn,
    onClick: () => handleOnClick('linkedin', linkForLinkedIn)
  }, props, {
    children: [/*#__PURE__*/_jsx(SocialShareIcon, {
      icon: SocialLinkedinIcon
    }), /*#__PURE__*/_jsx(FormattedMessage, {
      message: "learningCenter.socialShare.shareLinkedIn"
    })]
  }), "share-linkedin"), /*#__PURE__*/_jsxs(SocialShareListOption, Object.assign({
    href: linkForWhatsapp,
    onClick: () => handleOnClick('whatsapp', linkForWhatsapp)
  }, props, {
    children: [/*#__PURE__*/_jsx(SocialShareIcon, {
      icon: SocialWhatsappIcon
    }), /*#__PURE__*/_jsx(FormattedMessage, {
      message: "learningCenter.socialShare.shareWhatsApp"
    })]
  }), "share-whatsapp")];
};
const SocialShareListSubmenu = ({
  item,
  renderSubmenuRow = (shareMessage, itemName) => /*#__PURE__*/_jsxs(View, {
    children: [/*#__PURE__*/_jsx(Text, {
      color: "text-core-default",
      typography: "heading-50",
      children: shareMessage
    }), /*#__PURE__*/_jsx(Text, {
      color: "text-tertiary-default",
      children: itemName
    })]
  })
}) => {
  const {
    template: itemName,
    isShareable
  } = useSocialShareData(item);
  if (!isShareable) return null;
  return renderSubmenuRow( /*#__PURE__*/_jsx(FormattedMessage, {
    message: `learningCenter.socialShare.submenu.${item.type}`
  }), itemName);
};
export const useSocialShareList = _ref2 => {
  let {
      renderSubmenuRow,
      item
    } = _ref2,
    props = _objectWithoutPropertiesLoose(_ref2, _excluded2);
  const {
    playlistId,
    trackId,
    language
  } = item;
  const {
    shareLinks: itemShareLinks,
    loading: itemShareLinksLoading
  } = useShareLink(item);
  const {
    shareLinks: playlistShareLinks,
    loading: playlistShareLinksLoading
  } = useShareLink({
    id: playlistId,
    type: 'PLAYLIST',
    language
  }, !playlistId);
  const {
    shareLinks: trackShareLinks,
    loading: trackShareLinksLoading
  } = useShareLink({
    id: trackId,
    type: 'TRACK',
    language
  }, !trackId);
  const isContainedInPlaylist = !!playlistId;
  const isContainedInTrack = !!trackId; // lesson within a track has to be public to be shareable

  const loading = itemShareLinksLoading || playlistShareLinksLoading || trackShareLinksLoading;
  if (loading) return [];
  if (isContainedInPlaylist || isContainedInTrack) {
    // shared item is contained in a playlist or a track, so we need to
    // ask first which item to share (playlist, track or the lesson/short)
    const list = [];
    if (isContainedInPlaylist && playlistShareLinks) {
      list.push({
        label: /*#__PURE__*/_jsx(SocialShareListSubmenu, {
          item: {
            id: playlistId,
            type: 'PLAYLIST'
          },
          renderSubmenuRow: renderSubmenuRow
        }, "share-playlist-submenu"),
        options: getSocialShareListOptions(Object.assign({
          item: {
            id: playlistId,
            language,
            type: 'PLAYLIST'
          }
        }, props), playlistShareLinks)
      });
    }
    if (isContainedInTrack && trackShareLinks) {
      list.push({
        label: /*#__PURE__*/_jsx(SocialShareListSubmenu, {
          item: {
            id: trackId,
            language,
            type: 'TRACK'
          },
          renderSubmenuRow: renderSubmenuRow
        }, "share-track-submenu"),
        options: getSocialShareListOptions(Object.assign({
          item: {
            id: trackId,
            language,
            type: 'TRACK'
          }
        }, props), trackShareLinks)
      });
    }
    if (itemShareLinks) {
      list.push({
        label: /*#__PURE__*/_jsx(SocialShareListSubmenu, {
          item: item,
          renderSubmenuRow: renderSubmenuRow
        }, "share-item-submenu"),
        options: getSocialShareListOptions(Object.assign({
          item
        }, props), itemShareLinks)
      });
    }
    return list;
  }
  if (itemShareLinks) return getSocialShareListOptions(Object.assign({
    item
  }, props), itemShareLinks);
  return [];
};